import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { DocumentHeader, SITE_URL } from "../../components/Seo";
import { Logo } from "../../constants";
import { checkMail } from "../../utils/functions";
import { submitBrochureLead } from "../../services/leadCalls";
import { getFormUtm } from "../../utils/attribution";
import { ADS_CONVERSIONS, trackAdsConversion, trackLead } from "../../utils/adsConversions";
import styles from "./PartnerLanding.module.css";

const PDF_URL = "/brochures/awasource-partner-portal-brochure.pdf";
const UTM_KEYS = ["source", "medium", "campaign", "content", "term"];
const UTM_STORE = "awa_utm";

const VOLUMES = [
  { label: "Fewer than 50", value: 25 },
  { label: "50 – 200", value: 125 },
  { label: "200 – 1,000", value: 600 },
  { label: "More than 1,000", value: 1000 },
];

const BENEFITS = [
  "58 role-calibrated psychometric assessments",
  "Candidate Fit: rank every CV against your job spec",
  "Results in your dashboard and your system, in real time",
  "White-label by default — your brand on every candidate page",
];

const AUDIENCE = ["Recruitment agencies", "Heads of HR & talent", "HR-tech & ATS platforms", "Talent marketplaces", "Staffing firms"];

// UTM parameters from the ad click, remembered for the visit so they survive
// in-page navigation. Storage can be unavailable (private mode): fall back to the URL.
function readUtm(search) {
  const params = new URLSearchParams(search);
  const fromUrl = {};
  UTM_KEYS.forEach((k) => {
    const v = params.get(`utm_${k}`);
    if (v) fromUrl[k] = v.slice(0, 200);
  });
  try {
    if (Object.keys(fromUrl).length) {
      sessionStorage.setItem(UTM_STORE, JSON.stringify(fromUrl));
      return fromUrl;
    }
    const saved = JSON.parse(sessionStorage.getItem(UTM_STORE) || "{}");
    return saved && typeof saved === "object" ? saved : {};
  } catch (_) {
    return fromUrl;
  }
}

export default function PartnerLanding() {
  const { search, pathname } = useLocation();
  const utm = useMemo(() => readUtm(search), [search]);
  const [form, setForm] = useState({ fullName: "", email: "", companyName: "", volume: "", consent: false });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [serverError, setServerError] = useState("");

  useEffect(() => window.scrollTo?.(0, 0), []);

  const set = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.fullName.trim()) e.fullName = "Please enter your name.";
    if (!checkMail(form.email.trim())) e.email = "Please enter a valid work email.";
    if (!form.companyName.trim()) e.companyName = "Please enter your company.";
    if (!form.volume) e.volume = "Please choose a range.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    setServerError("");
    if (!validate()) return;
    setSubmitting(true);
    try {
      const stored = getFormUtm();
      const utmPayload = { ...utm };
      Object.entries(stored.utm || {}).forEach(([key, value]) => {
        if (value) utmPayload[key] = value;
      });
      const res = await submitBrochureLead({
        fullName: form.fullName.trim(),
        email: form.email.trim(),
        companyName: form.companyName.trim(),
        assesmentPerMonth: Number(form.volume),
        marketingConsent: form.consent,
        utm: utmPayload,
        landingPage: stored.landingPage || pathname,
      });
      trackLead({ content_name: "partner_portal_brochure" });
      trackAdsConversion(ADS_CONVERSIONS.BROCHURE_DOWNLOAD);
      setDone({ url: res?.data?.downloadUrl || PDF_URL, firstName: form.fullName.trim().split(/\s+/)[0] });
    } catch (err) {
      setServerError("Something went wrong. Please check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.page}>
      <DocumentHeader
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          name: "Awasource Partner Portal",
          description:
            "Psychometric tests, CV-to-job ranking, and live results under your brand. Built for agencies, HR teams, and hiring platforms.",
          brand: { "@type": "Organization", name: "Awasource", url: SITE_URL },
        }}
      />

      <nav className={styles.nav}>
        <div className={styles.container}>
          <Link to="/" className={styles.logo} aria-label="Awasource home">
            <Logo color="#ffffff" />
          </Link>
          <Link to="/api-integrations" className={styles.navLink}>
            Partner API →
          </Link>
        </div>
      </nav>

      <header className={styles.hero}>
        <div className={`${styles.container} ${styles.heroGrid}`}>
          <div>
            <span className={styles.badge}>Awasource Partner Portal &amp; API</span>
            <h1 className={styles.title}>
              Assess talent. Rank candidates. <span>Under your brand.</span>
            </h1>
            <p className={styles.sub}>
              Give your clients and hiring teams role-specific psychometric testing, explainable candidate ranking
              and real-time results — in our portal, or plugged straight into your own system.
            </p>
            <ul className={styles.inside}>
              {BENEFITS.map((t) => (
                <li key={t}>{t}</li>
              ))}
            </ul>
            <div className={styles.audience}>
              {AUDIENCE.map((a) => (
                <span key={a}>{a}</span>
              ))}
            </div>
          </div>

          <div className={styles.card} id="get-the-guide">
            {done ? (
              <div className={styles.thanks} role="status">
                <div className={styles.tick} aria-hidden="true">✓</div>
                <h2>Your brochure is ready{done.firstName ? `, ${done.firstName}` : ""}</h2>
                <p>We've also emailed you a copy so you can find it later.</p>
                <a className={styles.primary} href={done.url} download target="_blank" rel="noopener noreferrer">
                  Download the brochure (PDF)
                </a>
                <Link className={styles.secondary} to="/api-integrations#contact">
                  Book a 20-minute partner demo →
                </Link>
              </div>
            ) : (
              <form onSubmit={onSubmit} noValidate>
                <h2>Download the partner brochure</h2>
                <p className={styles.formSub}>Free PDF · instant download · no spam.</p>

                <label className={styles.label} htmlFor="for-name">Full name</label>
                <input id="for-name" className={styles.input} value={form.fullName} onChange={set("fullName")} autoComplete="name" maxLength={120} aria-invalid={!!errors.fullName} />
                {errors.fullName && <span className={styles.err}>{errors.fullName}</span>}

                <label className={styles.label} htmlFor="for-email">Work email</label>
                <input id="for-email" type="email" className={styles.input} value={form.email} onChange={set("email")} autoComplete="email" maxLength={254} aria-invalid={!!errors.email} />
                {errors.email && <span className={styles.err}>{errors.email}</span>}

                <label className={styles.label} htmlFor="for-company">Company</label>
                <input id="for-company" className={styles.input} value={form.companyName} onChange={set("companyName")} autoComplete="organization" maxLength={200} aria-invalid={!!errors.companyName} />
                {errors.companyName && <span className={styles.err}>{errors.companyName}</span>}

                <label className={styles.label} htmlFor="for-volume">Candidates you assess per month</label>
                <select id="for-volume" className={styles.input} value={form.volume} onChange={set("volume")} aria-invalid={!!errors.volume}>
                  <option value="">Choose a range</option>
                  {VOLUMES.map((v) => (
                    <option key={v.value} value={v.value}>{v.label}</option>
                  ))}
                </select>
                {errors.volume && <span className={styles.err}>{errors.volume}</span>}

                <label className={styles.consent}>
                  <input type="checkbox" checked={form.consent} onChange={set("consent")} />
                  <span>Email me a few follow-ups about the Partner Portal (a sample report, how partners go live) and occasional hiring insights. Unsubscribe anytime.</span>
                </label>

                {serverError && <div className={styles.serverErr}>{serverError}</div>}

                <button type="submit" className={styles.primary} disabled={submitting}>
                  {submitting ? "Preparing your brochure…" : "Get the brochure →"}
                </button>
                <p className={styles.privacy}>
                  We use your details to send the brochure and, only if you tick the box, the follow-up emails. See how we handle{" "}
                  <Link to="/privacy-policy#marketing-forms">form details</Link> in our privacy policy.
                </p>
              </form>
            )}
          </div>
        </div>
      </header>

      <section className={styles.preview}>
        <div className={styles.container}>
          <p className={styles.eyebrow}>A look inside the brochure</p>
          <div className={styles.pages}>
            {[1, 3, 4].map((p) => (
              <img key={p} src={`/brochures/for-preview-${p}.jpg`} alt={`Awasource partner brochure — page ${p}`} loading="lazy" />
            ))}
          </div>
        </div>
      </section>

      <section className={styles.cta}>
        <div className={styles.container}>
          <h2>Ready to add assessments to your hiring?</h2>
          <p>58 role-calibrated assessments, explainable candidate ranking and real-time results — under your brand.</p>
          <div className={styles.ctaRow}>
            <a href="#get-the-guide" className={styles.primary}>Download the brochure</a>
            <Link to="/api-integrations" className={styles.ghost}>Explore the Partner API</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
